import React, { useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Chip,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Assessment as AssessmentIcon,
  Book as BookIcon,
  BugReport as TestCaseIcon,
  ExpandMore as ExpandMoreIcon,
  Hub as HubIcon,
  Category as CategoryIcon,
} from '@mui/icons-material';
import ImpactCard from './ImpactCard';
import { extractJiraKey } from './ImpactAnalysisUtils';
import { SV } from '../../automation/stratovaiTestIds';

function deriveModuleKeys(impactData) {
  const rollup = impactData?.moduleRollup;
  if (rollup && typeof rollup === 'object') {
    const keys = Object.keys(rollup).filter(Boolean);
    if (keys.length) return [...new Set(keys)].sort();
  }
  const modImp = impactData?.moduleImpacts;
  if (modImp && typeof modImp === 'object') {
    const keys = Object.keys(modImp).filter(Boolean);
    if (keys.length) return [...new Set(keys)].sort();
  }
  const pkbs = impactData?.supportingProductKnowledge;
  const tags = new Set();
  if (Array.isArray(pkbs)) {
    pkbs.forEach((imp) => {
      const modules = imp?.modules || [];
      modules.forEach((m) => {
        const k = m && typeof m === 'object' ? (m.module_key || m.key || m.name) : m;
        if (k) tags.add(String(k).trim());
      });
    });
  }
  const fromAll = impactData?.impacts;
  if (Array.isArray(fromAll)) {
    fromAll.forEach((imp) => {
      if (imp?.type === 'test_case') return;
      const modules = imp?.modules || [];
      modules.forEach((m) => {
        const k = m && typeof m === 'object' ? (m.module_key || m.key || m.name) : m;
        if (k) tags.add(String(k).trim());
      });
    });
  }
  return [...tags].filter(Boolean).sort();
}

function areaDisplayName(area) {
  const name = area?.area_name ?? area?.area_id;
  if (name === undefined || name === null || name === '') return 'QA focus area';
  return String(name);
}

const QA_EXPAND_ACCORDION_SX = {
  mb: 1,
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 2,
  '&:before': { display: 'none' },
  boxShadow: 'none',
  bgcolor: 'background.paper',
  '& .MuiAccordionSummary-root': { color: 'text.primary' },
  '& .MuiAccordionDetails-root': {
    borderTop: '1px solid',
    borderColor: 'divider',
  },
};

/**
 * Impact Analysis modal — QA-focused hierarchy with supporting PKB evidence.
 */
const ImpactAnalysisModal = ({
  open,
  onClose,
  impactData,
  sourceItem,
  projectId,
  jiraBaseUrl,
  onViewPKB,
  onFeedback,
}) => {
  const sourceJiraKey = useMemo(() => {
    if (!sourceItem) return null;
    return extractJiraKey(sourceItem);
  }, [sourceItem]);

  const sourceTitle = useMemo(() => {
    if (!sourceItem) return 'Current PRD';
    const content = sourceItem.content || {};
    return content.name || content.title || sourceItem.title || 'Current PRD';
  }, [sourceItem]);

  const impactList = useMemo(() => {
    if (Array.isArray(impactData)) return impactData;
    if (Array.isArray(impactData?.impacts)) return impactData.impacts;
    return [];
  }, [impactData]);

  const qaImpactAreas = useMemo(() => {
    if (Array.isArray(impactData?.impactedAreas)) return impactData.impactedAreas;
    return [];
  }, [impactData]);

  const qaAreasSig = useMemo(
    () => qaImpactAreas.map((a) => String(a.area_id || a.area_name || '')).join('\u001f'),
    [qaImpactAreas]
  );

  const testFocus = impactData?.testFocus || null;
  const impactSummary = impactData?.impactSummary || null;
  const ontology = impactData?.diagnostics?.ontology || null;

  const moduleKeys = useMemo(() => deriveModuleKeys(impactData), [impactData]);

  const { pkbImpacts, confidentPkbImpacts, possiblePkbImpacts, testCaseImpacts } = useMemo(() => {
    const rawPkbs = Array.isArray(impactData?.supportingProductKnowledge)
      ? impactData.supportingProductKnowledge
      : null;

    const list = rawPkbs && rawPkbs.length > 0 ? rawPkbs : impactList.filter((i) => i?.type !== 'test_case');

    if (!Array.isArray(list)) {
      return { pkbImpacts: [], confidentPkbImpacts: [], possiblePkbImpacts: [], testCaseImpacts: [] };
    }

    const pkb = [];
    const confident = [];
    const possible = [];
    const testCases = [];

    impactList.forEach((impact) => {
      if (impact?.type === 'test_case') testCases.push(impact);
    });

    list.forEach((impact) => {
      pkb.push(impact);
      if (impact.impact_bucket === 'possible') {
        possible.push(impact);
      } else {
        confident.push(impact);
      }
    });

    return {
      pkbImpacts: pkb,
      confidentPkbImpacts: confident,
      possiblePkbImpacts: possible,
      testCaseImpacts: testCases,
    };
  }, [impactData, impactList]);

  const confidenceSummary = impactSummary?.confidence_summary || {};

  const [qaRowExpanded, setQaRowExpanded] = useState({});
  const [qaListScope, setQaListScope] = useState('all');
  const [supportingPkbOpen, setSupportingPkbOpen] = useState(false);
  const [testCasesOpen, setTestCasesOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setQaRowExpanded({});
    setQaListScope('all');
  }, [open, qaAreasSig]);

  useEffect(() => {
    if (!open) return;
    setSupportingPkbOpen(false);
    setTestCasesOpen(false);
  }, [open, qaAreasSig, pkbImpacts.length, testCaseImpacts.length]);

  const qaIndicesVisible = useMemo(() => {
    if (qaListScope === 'top3') {
      return qaImpactAreas.map((_, i) => i).slice(0, 3);
    }
    return qaImpactAreas.map((_, i) => i);
  }, [qaListScope, qaImpactAreas]);

  const expandAllQa = () => {
    setQaRowExpanded((prev) => {
      const next = { ...prev };
      qaIndicesVisible.forEach((i) => {
        next[i] = true;
      });
      return next;
    });
  };

  const collapseAllQa = () => {
    setQaRowExpanded((prev) => {
      const next = { ...prev };
      qaIndicesVisible.forEach((i) => {
        next[i] = false;
      });
      return next;
    });
  };

  const toggleQaListScope = () => {
    setQaListScope((prev) => {
      const next = prev === 'top3' ? 'all' : 'top3';
      if (next === 'top3') {
        setQaRowExpanded(Object.fromEntries(qaImpactAreas.map((_, i) => [i, i < 3])));
      } else {
        setQaRowExpanded((p) => {
          const out = { ...p };
          qaImpactAreas.forEach((_, i) => {
            if (i >= 3) out[i] = false;
          });
          return out;
        });
      }
      return next;
    });
  };

  const handleFeedback = (impact, label) => {
    if (onFeedback && sourceItem) {
      const targetTitle = impact?.title || impact?.jira_issue_key || 'Unknown';
      onFeedback(sourceItem.knowledge_id, targetTitle, label);
    }
  };

  const handleViewPKB = (impact) => {
    if (onViewPKB) {
      onViewPKB(impact);
    }
  };

  const renderImpactCard = (impact, index) => (
    <ImpactCard
      key={`${impact.jira_issue_key || impact.title || 'impact'}-${index}`}
      impact={impact}
      sourceItem={sourceItem}
      sourceJiraKey={sourceJiraKey}
      sourceTitle={sourceTitle}
      jiraBaseUrl={jiraBaseUrl}
      projectId={projectId}
      onViewPKB={handleViewPKB}
      onFeedback={handleFeedback}
    />
  );

  const hasAnyPrimary =
    qaImpactAreas.length > 0 ||
    moduleKeys.length > 0 ||
    pkbImpacts.length > 0 ||
    testCaseImpacts.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        'data-testid': SV.pk.impactModal,
        sx: { minHeight: '60vh' },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon color="primary" />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="span">
              Impact Analysis
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              QA-ordered view: test focus and modules first; product knowledge as supporting evidence.
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent data-testid={SV.pk.impactModalResults}>
        {/* —— Impact summary —— */}
        <Paper
          elevation={0}
          sx={(theme) => ({
            p: 2,
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor:
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.08)
                : alpha(theme.palette.primary.main, 0.06),
          })}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.primary' }}>
            Impact summary
          </Typography>
          <Stack spacing={1}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                Changed item
              </Typography>
              <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                {sourceJiraKey ? `${sourceJiraKey} · ` : ''}
                {sourceTitle}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                QA areas by level
              </Typography>
              <Chip size="small" variant="outlined" label={`High ${Number(confidenceSummary.high ?? 0)}`} color="error" />
              <Chip
                size="small"
                variant="outlined"
                label={`Medium ${Number(confidenceSummary.medium ?? 0)}`}
                color="warning"
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Possible ${Number(confidenceSummary.possible ?? 0)}`}
                color="info"
              />
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Domain
                </Typography>
                <Chip size="small" label={ontology?.selected_domain || '—'} variant="outlined" />
              </Box>
              {ontology?.domain_selection_reason ? (
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                  {ontology.domain_selection_reason}
                </Typography>
              ) : null}
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              <Typography variant="body2" color="text.primary">
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {qaImpactAreas.length}
                </Box>{' '}
                QA {qaImpactAreas.length === 1 ? 'area' : 'areas'}
              </Typography>
              <Typography variant="body2" color="text.primary">
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {moduleKeys.length}
                </Box>{' '}
                affected {moduleKeys.length === 1 ? 'module' : 'modules'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {pkbImpacts.length} supporting PKB {pkbImpacts.length === 1 ? 'item' : 'items'}
                {testCaseImpacts.length > 0
                  ? ` · ${testCaseImpacts.length} test ${testCaseImpacts.length === 1 ? 'case' : 'cases'}`
                  : ''}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {!hasAnyPrimary ? (
          <Box
            sx={(theme) => ({
              p: 2,
              mb: 3,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.04),
            })}
          >
            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
              No strong matches surfaced.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Weak possible matches may be filtered. Try broadening PRD detail or approved PKB coverage.
            </Typography>
          </Box>
        ) : null}

        {/* —— Impacted QA areas —— */}
        {qaImpactAreas.length > 0 ? (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                mb: 1,
                rowGap: 1,
              }}
            >
              <HubIcon color="success" fontSize="small" />
              <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'text.primary' }}>
                Impacted QA areas / test focus
              </Typography>
              <Chip size="small" label={`${qaImpactAreas.length}`} variant="outlined" />
              <Box sx={{ flex: '1 1 120px' }} />
              <Button size="small" variant="text" onClick={expandAllQa} sx={{ minWidth: 'auto' }}>
                Expand all
              </Button>
              <Button size="small" variant="text" onClick={collapseAllQa} sx={{ minWidth: 'auto' }}>
                Collapse all
              </Button>
              {qaImpactAreas.length > 3 ? (
                <Button size="small" variant="text" onClick={toggleQaListScope} sx={{ minWidth: 'auto' }}>
                  {qaListScope === 'top3' ? 'Show all' : 'Show top 3'}
                </Button>
              ) : null}
            </Box>

            {Array.isArray(testFocus?.functional_checks) && testFocus.functional_checks.length > 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, maxWidth: '900px' }}>
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Highlights:{' '}
                </Box>
                {testFocus.functional_checks.slice(0, 6).join(' · ')}
              </Typography>
            ) : null}

            {qaIndicesVisible.map((index) => {
              const area = qaImpactAreas[index];
              const pkbs = area.supporting_pkb_entries || [];
              const pkbCount = pkbs.length;
              const sourcesLabel = Array.isArray(area.supporting_sources)
                ? area.supporting_sources
                    .map((s) => (s == null ? '' : String(s).trim()))
                    .filter(Boolean)
                    .join(', ')
                : '';
              const evidLines = pkbs
                .slice(0, 4)
                .map((e) => e.jira_issue_key || e.title)
                .filter(Boolean);
              const isExpanded = qaRowExpanded[index] ?? index < 3;
              return (
                <Accordion
                  key={`${area.area_id || area.area_name || 'area'}-${index}`}
                  expanded={isExpanded}
                  onChange={(_, expandedNext) =>
                    setQaRowExpanded((prev) => ({ ...prev, [index]: expandedNext }))
                  }
                  sx={QA_EXPAND_ACCORDION_SX}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                    sx={{ py: 0.5, minHeight: 48, '& .MuiAccordionSummary-content': { my: 0.5 } }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, pr: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {areaDisplayName(area)}
                      </Typography>
                      <Chip
                        size="small"
                        label={area.impact_level || 'Possible'}
                        color={
                          area.impact_level === 'High'
                            ? 'error'
                            : area.impact_level === 'Medium'
                              ? 'warning'
                              : 'info'
                        }
                      />
                      {area.area_type ? (
                        <Chip size="small" label={String(area.area_type)} variant="outlined" />
                      ) : null}
                      {typeof area.confidence === 'number' ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Confidence ${Math.round(area.confidence * 100)}%`}
                        />
                      ) : null}
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${pkbCount} supporting PKB ${pkbCount === 1 ? 'ref' : 'refs'}`}
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails
                    sx={(theme) => ({
                      pt: 1.5,
                      pb: 1.5,
                      bgcolor:
                        theme.palette.mode === 'dark'
                          ? alpha(theme.palette.background.paper, 0.5)
                          : alpha(theme.palette.text.primary, 0.03),
                    })}
                  >
                    {area.why_impacted ? (
                      <Typography variant="body2" sx={{ color: 'text.primary', mb: 1.25, lineHeight: 1.55 }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          Why impacted:{' '}
                        </Box>
                        {area.why_impacted}
                      </Typography>
                    ) : null}
                    {Array.isArray(area.suggested_test_focus) && area.suggested_test_focus.length > 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Suggested test focus:{' '}
                        </Box>
                        {area.suggested_test_focus.join('; ')}
                      </Typography>
                    ) : null}
                    {evidLines.length > 0 ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Evidence: {evidLines.join(' · ')}
                      </Typography>
                    ) : null}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                      {sourcesLabel ? (
                        <Chip size="small" variant="outlined" label={sourcesLabel} />
                      ) : null}
                      {area.automation_candidate ? (
                        <Chip size="small" label="Automation candidate" color="success" variant="outlined" />
                      ) : null}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <HubIcon color="action" />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Impacted QA areas / test focus
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              No ontology-linked QA areas for this run. Use supporting product knowledge below for indirect
              coverage signals.
            </Typography>
          </Box>
        )}

        {/* —— Affected modules —— */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <CategoryIcon color="primary" fontSize="small" />
            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'text.primary' }}>
              Affected modules
            </Typography>
            {moduleKeys.length > 0 ? (
              <Chip size="small" label={`${moduleKeys.length}`} variant="outlined" />
            ) : null}
          </Box>
          {moduleKeys.length > 0 ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {moduleKeys.map((key) => (
                <Chip key={key} size="small" label={key} variant="outlined" color="primary" />
              ))}
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
              No module tags on matched items. Modules appear when your PKB or story metadata includes module
              taxonomy.
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* —— Supporting product knowledge (collapsed by default) —— */}
        <Box sx={{ mb: 2 }}>
          {pkbImpacts.length > 0 ? (
            <Accordion
              expanded={supportingPkbOpen}
              onChange={(_, next) => setSupportingPkbOpen(next)}
              sx={{
                ...QA_EXPAND_ACCORDION_SX,
                mb: 0,
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                sx={{ py: 0.5, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
              >
                <BookIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Supporting product knowledge ({pkbImpacts.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails
                sx={(theme) => ({
                  pt: 1,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.5)
                      : alpha(theme.palette.text.primary, 0.02),
                })}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                  PKB matches that support the QA areas above — expand for evidence and traceability.
                </Typography>
                {confidentPkbImpacts.length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'text.primary' }}>
                      Confident / secondary
                    </Typography>
                    {confidentPkbImpacts.map(renderImpactCard)}
                  </Box>
                ) : null}

                {possiblePkbImpacts.length > 0 ? (
                  <Accordion
                    defaultExpanded={false}
                    sx={{
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      '&:before': { display: 'none' },
                      boxShadow: 'none',
                      '& .MuiAccordionSummary-root': {
                        color: 'text.primary',
                      },
                      '& .MuiAccordionDetails-root': {
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                      sx={{
                        '& .MuiAccordionSummary-content': { overflow: 'hidden' },
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        Possible impacts ({possiblePkbImpacts.length})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ bgcolor: 'background.default' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Broader or indirect relationships — review after primary QA areas.
                      </Typography>
                      {possiblePkbImpacts.map(renderImpactCard)}
                    </AccordionDetails>
                  </Accordion>
                ) : null}
              </AccordionDetails>
            </Accordion>
          ) : (
            <Box
              sx={(theme) => ({
                p: 1.5,
                textAlign: 'center',
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.04),
              })}
            >
              <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                Supporting product knowledge (0)
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                No supporting product knowledge items in this result.
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* —— Test cases (collapsed by default) —— */}
        <Accordion
          expanded={testCasesOpen}
          onChange={(_, next) => setTestCasesOpen(next)}
          sx={{
            ...QA_EXPAND_ACCORDION_SX,
            mb: 0,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
            sx={{ py: 0.5, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
          >
            <TestCaseIcon color="secondary" fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Impacted test cases ({testCaseImpacts.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            sx={(theme) => ({
              pt: 1,
              bgcolor:
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.background.paper, 0.5)
                  : alpha(theme.palette.text.primary, 0.02),
            })}
          >
            {testCaseImpacts.length > 0 ? (
              testCaseImpacts.map((impact, index) => (
                <ImpactCard
                  key={index}
                  impact={impact}
                  sourceItem={sourceItem}
                  sourceJiraKey={sourceJiraKey}
                  sourceTitle={sourceTitle}
                  jiraBaseUrl={jiraBaseUrl}
                  projectId={projectId}
                  onViewPKB={handleViewPKB}
                  onFeedback={handleFeedback}
                />
              ))
            ) : (
              <Box
                sx={(theme) => ({
                  p: 1.5,
                  textAlign: 'center',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.04),
                })}
              >
                <Typography variant="body2" color="text.primary">
                  No linked test cases found for this item yet.
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions>
        <Button data-testid={SV.pk.impactModalClose} onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImpactAnalysisModal;
